/**
 * @file  src\pages\Home.js
 * @brief  This file is responsible for home page.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useRef, useCallback, useMemo, createContext } from "react";
import _ from "lodash";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import LeftpanelTab from "../../components/Onboarding/Dashboard/LeftpanelTab";
import LeftpanelTabContent from "../../components/Onboarding/Dashboard/v2/LeftpanelTabContent";
import SiteFooter from "../../components/Onboarding/SiteFooter";
import BackToTopButton from "../../components/Common/BackToTopButton";
import useScrollResetOnMount from "../../hooks/useScrollResetOnMount";
import PropertyListing from "../../components/Onboarding/Dashboard/v2/PropertyListing";
import useListingAiRanking from "../../hooks/useListingAiRanking";
import { sortMatchesByScoreDesc } from "../../helpers/matchSort";
import useAiSyncStatus from "../../hooks/useAiSyncStatus";
import { useQueryClient } from "@tanstack/react-query";
import Offcanvas from "react-bootstrap/Offcanvas";
import SelectDropDown from "../../components/Common/SelectDropDown";
import MatchTutorial from "../../components/Tutorial/MatchTutorialModal";
import {
  showAgentPreferenceModal,
} from "../../reducers/ui";
import {
  BUY,
  WANT_AD_KEY,
  WANT_AD_PAGE_LIMIT,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
  HOME_PAGE,
  INVENTORY_KEY,
  STRENGTHEN_INVENTORY_KEY,
  CLIENT_DETAILS_KEY,
  LONG_RENT,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
} from "../../constants/common";
import { getWantAds, getWantAdsPublic } from "../../services/wantAdService";
import { getHasPublicSyndication } from "../../services/v2/wantAdService";
import { getMatches } from "@/services";
import HomeLoadingSkeleton from "../HomeLoadingSkeleton";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  loggedInUser,
  removeMultipleLocalStorageItems,
  checkTutorialDisplayStatus,
  getUserType,
  getTitleText,
} from "../../helpers/common";
import {
  resolveKnownUserLocation,
  shouldRevalidatePersistedLocation,
  persistCheckedLocation,
} from "../../helpers/knownUserLocation";
import {
  favouriteWantAd,
  unFavouriteWantAd,
} from "../../services/wantAdService";
import { toast } from "react-toastify";
import {
  getAgentsList,
  getUserInventoryCount,
} from "../../services/wantAdService";
import { handleTextBoxChange } from "../../actions/common";
import { pageURLs } from "@/constants";
import { getUserId } from "@/helpers";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import GeoLocation from "../../components/Common/GeoLocation";
import { usePrevious } from "../../hooks/usePrevious";
import {
  buildLocationsParam,
  buildLocationLabel,
  resolveLocationSelection,
} from "../../helpers/locationSearch";
import { computeActiveFilterCount } from "../../helpers/activeFilterCount";
import { saveTutorialStatus } from "../../services/commonServices";
import { checkGitchaAvailable } from "../../services/commonServices";
import GitchaAvailableLocations from "../../components/GitchaAvailableLocationsScreen";
import RequestStayAvailability from "../../components/Common/RequestStayAvailability";
import SearchLocationScreen from "../../components/SearchLocationScreen";
import FilterLocationSearch from "../../components/Onboarding/Dashboard/FilterLocationSearch";
import FeedMobileBar from "../../components/AgentDashboard/FeedMobileBar";
import WhatsNew from "../../components/WhatsNew";
import { hasSeenRelease } from "../../components/WhatsNew/whatsNewSteps";
import HeroSplash from "../../components/HeroSplash/HeroSplash";
import SearchTransition from "../../components/HeroSplash/SearchTransition";
import PreLaunchCityRequestModal from "../../components/Common/PreLauchCityRequestModal";
import WelcomeModal from "../../components/Onboarding/WelcomeModal";
import AgentLiketoStartModal from "@/components/Onboarding/UserRegister/AgentLiketoStartModal";
import { PageWrapper } from "@/components/PageWrapper";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import GitchaNotAvailableModal from "@/components/Common/GitchaNotAvailableModal";
import FilterIcon from "../../images/filter-icon.svg";
import { useDispatch } from "react-redux";
import MatchHomeTabSkeleton from "@/components/Onboarding/Dashboard/MatchSkeleton";
import TenantLoginPage from "@/components/Common/TenantLoginPage/TenantLoginPage";
import FeedEmptyState from "@/components/AgentDashboard/FeedEmptyState/FeedEmptyState";
import { isMlsRestrictedTenant } from "@/helpers/tenant";
import { formatResultsCountLabel } from "@/helpers/resultsCountLabel";
import { safeRedirectPath } from "@/helpers/postLoginRedirect";
import { appendWantAdPage } from "@/helpers/feedPaging";
import { useAppConfig } from "@/appConfig";

export const filterContext = createContext();

// The newly-onboarded agent's syncing state. While `gated` their properties
// are held back entirely, so they cannot land on one whose matches do not
// exist yet; after the first slice is ready the same banner keeps explaining
// the rest, and the properties become selectable.
const AiSyncBanner = ({ gated, settled, total }) => (
  <div
    className={`ai-sync-banner${gated ? " ai-sync-banner--gated" : ""}`}
    role="status"
    aria-live="polite"
  >
    <span className="ai-sync-banner__spark" aria-hidden="true">
      ✦
    </span>
    <div className="ai-sync-banner__body">
      <strong className="ai-sync-banner__title">
        {gated
          ? "Syncing your listings and finding buyer matches"
          : "Still scoring your listings"}
      </strong>
      <span className="ai-sync-banner__text">
        {gated
          ? "We’re pulling your active listings and matching each one against live buyer demand in your market."
          : "Your ready listings are available now. The rest appear as their scoring finishes."}
        {total > 0 ? ` (${settled} of ${total} ready)` : ""}
      </span>
    </div>
  </div>
);

const Home = () => {

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const AdTypeFromUrl = searchParams.get("AdType");
  const showAgentModal = searchParams.get("showAgentModal");
  const tutorialPage = 1;
  const myRef = useRef(null);
  const navigate = useNavigate();

  const [ssoError] = useState(() => {
    const state = location.state;
    if (state?.ssoError) {
      window.history.replaceState({}, "");
      return state.ssoError;
    }
    return null;
  });

  const [mlsLinkState, setMlsLinkState] = useState(() => {
    const state = location.state;
    if (state?.pendingMlsLink && state?.pendingToken) {
      window.history.replaceState({}, "");
      return {
        show: true,
        pendingToken: state.pendingToken,
        maskedEmail: state.maskedEmail || "",
        redirect: state.redirect || null,
      };
    }
    return { show: false, pendingToken: null, maskedEmail: "", redirect: null };
  });
  const browserLocation = getLocalStorage("userLocation");

  // Session filter persistence - persists across navigation, clears on app close
  let savedSession = null;
  try {
    const data = sessionStorage.getItem("homeFilterSession");
    if (data) savedSession = JSON.parse(data);
  } catch { }
  const isRestoredRef = useRef(!!savedSession);
  // Falls back to the persisted market (localStorage) when there's no active
  // tab session, so a new tab or a fresh browser launch restores it instead
  // of re-prompting for location.
  const knownUserLocation = resolveKnownUserLocation(
    savedSession?.userLocation,
    browserLocation,
  );
  // Only a session-restored location has already been through the
  // availability check this browser session (its isFromAvailableCities was
  // set on the live state object before this render). A persisted-only
  // restore (new tab / fresh browser) has NOT: setLocalStorage("userLocation", ...)
  // in handleUserLocation below runs, and serializes, before the
  // isFromAvailableCities check resolves, so the localStorage copy never
  // carries that flag. Only skip handleUserLocation's own check for the
  // session-restored case - the persisted-only case still needs it (see the
  // mount effect below), just without a geolocation permission prompt.
  const hasRestoredLocation = useRef(!!savedSession?.userLocation);
  // Fixed at mount, unlike hasRestoredLocation (a one-shot guard consumed on
  // first use) - stays true for GeoLocation's lifetime so clearing the
  // market later doesn't cause it to re-request the browser's permission.
  const skipGeolocationRef = useRef(!!knownUserLocation);
  // Guards fetchWantAdsMatchingGitchaGenie against out-of-order responses:
  // selecting property A then quickly B fires two overlapping requests, and
  // without this a slow response for A can land after B's and overwrite it.
  const matchRequestIdRef = useRef(0);
  const queryClient = useQueryClient();
  // A selected listing the warm-up has not settled yet (e.g. ingested minutes
  // ago). Drives the "AI is ranking" strip. No timers: opening a listing
  // computes nothing, so there is nothing to wait on.
  const aiSelectionKeyRef = useRef(null);
  const resetScrollToTop = useScrollResetOnMount();

  const [showTransition, setShowTransition] = useState(false);
  const [transitionCity, setTransitionCity] = useState("");
  const handleTransitionComplete = useCallback(() => setShowTransition(false), []);

  const dispatch = useDispatch();

  const { mls, isGitchaDefaultWebsite } = useCurrentWebsite();
  const isBlsFeed = !!mls && !isGitchaDefaultWebsite;

  const options = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "highPrice", label: "Highest price" },
    { value: "lowPrice", label: "Lowest price" },
  ];
  // Match mode leads with relevance — the AI ordering when the listing is
  // ranked. Selecting a property resets the sort to this; the feed's last
  // sort ("Newest") used to leak in and silently veto the AI list.
  const MATCH_SORT_DEFAULT = { value: "bestMatch", label: "Best match" };
  const matchOptions = [MATCH_SORT_DEFAULT, ...options];
  const [showMatchTutorial, setMatchTutorial] = useState(false);
  const [currPage, setCurrPage] = useState(1); // storing current page number
  const [adList, setAdList] = useState([]);
  const [adListGitchaGenie, setAdListgitchaGenie] = useState([]);
  // Below-floor / weak AI picks for the selected property - rendered under a
  // "More suggestions" divider after the Best fits, never part of the count.
  const [aiSuggestionsGenie, setAiSuggestionsGenie] = useState([]);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [adType, setAdType] = useState(
    AdTypeFromUrl
      ? AdTypeFromUrl
      : savedSession?.filterQuery?.adType || BUY,
  );
  const filterTabKey = getLocalStorage("FilterTabKey");
  const previousBudgetMin = getLocalStorage("priceRangeMatch");
  const [totalCount, setTotalCount] = useState(null);
  const [totalCountMatch, setTotalCountMatch] = useState(null);
  const user = loggedInUser();
  // loggedInUser() re-parses localStorage on every render, so `user` is a fresh
  // object each time and is useless as an effect dependency - an effect keyed to
  // it re-runs every render, and any cleanup it has runs with it. The id is a
  // primitive that identifies the same thing, so effects key off this instead.
  const userId = user?.userInfo?.userId ?? null;
  const userType = getUserType();
  const { tenant } = useAppConfig() || {};
  const mlsRestricted = isMlsRestrictedTenant(tenant);
  const [sessionData, setSessionData] = useState(
    getLocalStorage(USER_INFO_KEY),
  );

  const [results, setResults] = useState([]);
  //form States on  agent client search
  const [formData, setFormData] = useState("");
  const [reloadAgents, setReloadAgents] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showNoResult, setShowNoResult] = useState(false);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [offCanvaShow, setOffCanvaShow] = useState(false);
  // Which job the agent opened the mobile drawer to do. The drawer holds the
  // market, the matches and the filters, so opening it without a focus meant
  // landing on whichever happened to be first.
  const [drawerFocus, setDrawerFocus] = useState("filters");
  // Every drawer trigger must go through this, not setOffCanvaShow directly -
  // drawerFocus is sticky state, so a trigger that skips it reopens the
  // drawer on whatever focus a previous visit left behind (e.g. "My matches"
  // stuck on a filter empty-state's filter button).
  const openDrawer = (focus) => {
    setDrawerFocus(focus);
    setOffCanvaShow(true);
  };
  // The mobile market picker expands inline in the feed bar rather than in a
  // sheet, so the agent sees their markets and edits them in place.
  const [marketPickerOpen, setMarketPickerOpen] = useState(false);
  // The what's-new tour lives here rather than in the shell because every step
  // targets an element on the feed - opening it anywhere else would spotlight
  // nothing. The header asks for it by event so the two trees stay decoupled.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  // Auto-open fires once per mount. Without this, dismissing while
  // ?whatsnew=1 is on the URL re-satisfies the effect and it reopens forever.
  const whatsNewAutoOpenedRef = useRef(false);
  // Set when a re-entry request (menu item, same-page dispatch) arrives while
  // the feed is still loading, so it can be honored once loading clears
  // instead of being silently dropped.
  const whatsNewPendingRef = useRef(false);
  const [marketSearchText, setMarketSearchText] = useState("");
  // Desktop Sort and Filter dropdown. Separate from offCanvaShow, which is the
  // mobile drawer and has its own triggers elsewhere on this page.
  const [sortFilterOpen, setSortFilterOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(knownUserLocation);
  // Extra cities the buyer added on top of the primary search location.
  // userLocation stays the sole gate for availability/empty-state; these
  // only widen the feed's geo search once a primary location is active.
  const [additionalLocations, setAdditionalLocations] = useState(
    savedSession?.additionalLocations || [],
  );
  const [isGitchaGenieFilter, setIsGitchaGenieFilter] = useState(
    filterTabKey === "match" ? true : false,
  );

  const [selectedProperty, setSelectedProperty] = useState(null);
  // AI reasons for the selected property's pairings; inert until the
  // tenant's display switch is on.
  const listingAiRank = useListingAiRanking({
    mlsListingKey:
      selectedProperty?.rowType === "mls" ? selectedProperty?.ListingKey : null,
    inventoryId:
      selectedProperty?.rowType === "mls" ? null : selectedProperty?.Inv_Id,
  });
  // Whether this user's matches are still being built after onboarding. Same
  // query key as the Property Selector, so the banner and the list of
  // selectable properties can never contradict each other.
  const aiSync = useAiSyncStatus();
  // Match mode follows an explicit gauge-demand action (LeftpanelTabContentRight),
  // never an inferred one - the property list auto-selects its first entry on
  // load, which would otherwise drop every agent straight into match mode with
  // an empty feed. Owned here, not inside LeftpanelTab: that component mounts
  // twice (drawer + rail), and local state reset to false on the second mount,
  // which exited match mode the instant the filter drawer opened.
  const [gaugeDemand, setGaugeDemand] = useState(false);
  const [reload, setReload] = useState(false);
  const [sortParam, setSortParam] = useState(savedSession?.sortParam || options[0]);
  const [gitchaNotAvailble, setGitchaNotAvailable] = useState(
    userLocation?.isFromAvailableCities ? true : false,
  );
  const wantAdCount = adList?.length;
  const wantAdCountGitchaGenie = adListGitchaGenie?.length;
  const locationString = getLocalStorage("LocationString");

  // Sorted on the score the card renders, so the list agrees with the badges.
  const aiEntryFor = useCallback(
    (wa) => listingAiRank.byWantAdId.get(Number(wa?.Want_ad_Id)) || null,
    [listingAiRank],
  );
  const sortedAdListGitchaGenie = useMemo(
    () => sortMatchesByScoreDesc(adListGitchaGenie, aiEntryFor),
    [adListGitchaGenie, aiEntryFor],
  );
  const sortedAiSuggestionsGenie = useMemo(
    () => sortMatchesByScoreDesc(aiSuggestionsGenie, aiEntryFor),
    [aiSuggestionsGenie, aiEntryFor],
  );
  const [isCityInPreLaunch, setIsCityInPreLaunch] = useState(false);
  const [PreLaunchStateName, setPreLaunchStateName] = useState(null);
  const [showGitchaGeineLabel, setShowGitchaGeineLabel] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Advancing a page writes filterQuery directly AND through the currPage
  // effect, each a fresh object identity - without this the same page is
  // requested twice.
  const lastFeedQueryRef = useRef(null);
  // Reported up from LeftpanelTabContentRight, which owns the property list.
  const [matchInventory, setMatchInventory] = useState({
    resolved: false,
    hasProperties: false,
  });

  const [showRequestStayPopuop, setShowRequestStayPopup] = useState(false);
  const [showGitchaNotAvailablePopup, setShowGitchaNotAvailablePopup] = useState(false);
  const handleStayRequestPopupClose = () => {
    setShowRequestStayPopup(false);
  };
  /**
   * To handle user's default location when location permission is enabled
   * @param {*} location
   */
  const handleUserLocation = async (location) => {
    // Skip browser geolocation override when we have a restored session location
    if (hasRestoredLocation.current) {
      hasRestoredLocation.current = false;
      return;
    }
    // A BLS feed defaults to its whole MLS; where the agent happens to be
    // sitting is not a market to narrow it to.
    if (isBlsFeed) return;
    if (location) {

      const reqPayLoad = {
        county: location.county,
        state: location.state,
      };
      await checkGitchaAvailable(reqPayLoad).then((data) => {
        const isAvailable = _.get(data, "result") == true;
        const checked = persistCheckedLocation(location, isAvailable, {
          set: setLocalStorage,
          remove: removeLocalStorage,
        });
        if (isAvailable) {
          setGitchaNotAvailable(false);
          if (!locationString || locationString == "") {
            setUserLocation(checked);
          }
        } else {
          if (!locationString || locationString == "") {
            setUserLocation(null);
          }
          setGitchaNotAvailable(true);
        }
      });
    } else {
      setUserLocation(null);
    }

  }
  // A persisted-only location (no active tab session) was never run through
  // the availability check above - GeoLocation's own effect is skipped for it
  // (skipGeolocationRef), so nothing else calls handleUserLocation for this
  // case. Without this, a returning user in a new tab/fresh browser would
  // hit the "not available" empty state (userLocation?.isFromAvailableCities)
  // even in a covered city, since the persisted copy never carries that flag.
  useEffect(() => {
    if (shouldRevalidatePersistedLocation(savedSession?.userLocation, browserLocation)) {
      handleUserLocation(browserLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  let showNotification = checkTutorialDisplayStatus(tutorialPage);
  const [showNotificationIcon, setNotificationIcon] = useState(false);
  const PRICE_MIN = 0;
  const PRICE_MAX = null;
  const SQFT_MIN = null;
  const SQFT_MAX = null;
  const initialQuery =
    adType == "STR"
      ? {
        adType: adType,
        propertyType: [],
        alwpets: null,
        adaAccess: null,
        pvtBath: null,
        pvtEntrance: null,
        typeOfPlace: [10, 11, 6],
        priceMin: PRICE_MIN,
        priceMax: PRICE_MAX,
        sortType: sortParam?.value,
        bedrooms: "",
        beds: "",
        baths: "",
        sqrftMin: SQFT_MIN,
        sqrftMax: SQFT_MAX,
        lat: _.get(userLocation, "latitude", ""),
        long: _.get(userLocation, "longitude", ""),
        city: _.get(userLocation, "city", ""),
        state: _.get(userLocation, "state", ""),
        locations: buildLocationsParam(userLocation, additionalLocations),
        search: "",
        page: currPage,
        limit: WANT_AD_PAGE_LIMIT,
        fromDate: null,
        toDate: null,
        agentOnly: false,
        mls,
      }
      : adType == "LTR"
        ? {
          adType: adType,
          propertyType: [],
          typeOfPlace: [10, 11, 6],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          search: "",
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          locations: buildLocationsParam(userLocation, additionalLocations),
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
          mls,
        }
        : {
          adType: adType,
          propertyType: [],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          locations: buildLocationsParam(userLocation, additionalLocations),
          search: "",
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
          mls,
        };

  const initialQueryGitchaGenie = {
    inventoryId: selectedProperty?.Inv_Id,
    mlsListingKey:
      selectedProperty?.rowType === "mls" ? selectedProperty?.ListingKey : null,
    adType: adType,
    city: selectedProperty?.City,
    state: selectedProperty?.State,
    priceMin: PRICE_MIN,
    priceMax: PRICE_MAX,
    sortType: sortParam?.value,
    page: currPage,
    limit: WANT_AD_PAGE_LIMIT,
  };

  const [filterQuery, setFilterQuery] = useState(
    savedSession?.filterQuery
      ? { ...savedSession.filterQuery, page: 1 }
      : initialQuery
  );
  const [filterQueryGitchaGenie, setFilterQueryGitchaGenie] = useState(
    initialQueryGitchaGenie,
  );

  // Shown on the Sort and Filter control so a narrowed feed is legible without
  // opening it - the pattern Faire, Mercor and Unity all use. Reads whichever
  // query is actually driving the visible feed: match mode filters through
  // filterQueryGitchaGenie (see the Sort and Filter panel below), and a badge
  // reading filterQuery there would show stale general-feed filters instead.
  const activeFilterCount = useMemo(
    () =>
      computeActiveFilterCount(
        isGitchaGenieFilter ? filterQueryGitchaGenie : filterQuery,
      ),
    [isGitchaGenieFilter, filterQueryGitchaGenie, filterQuery],
  );

  // Save filter state to sessionStorage (called from event handlers, not useEffect)
  const saveFilterSession = (fq, ul, sp, al) => {
    // Don't persist a session from the BLS login page (logged-out /home renders
    // TenantLoginPage) - it stores an empty location and poisons the restore
    // gate after sign-in.
    if (mlsRestricted && !user) return;
    try {
      sessionStorage.setItem("homeFilterSession", JSON.stringify({
        filterQuery: fq,
        userLocation: ul,
        sortParam: sp,
        additionalLocations: al,
      }));
    } catch { }
  };

  let DEFAULT_MAX_BUDGET;
  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
  }

  const priceMinGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[0]
    : null;
  const priceMaxGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[1] == DEFAULT_MAX_BUDGET
      ? ""
      : previousBudgetMin[1]
    : null;

  const previousUserLocation = usePrevious(userLocation);
  const previousAdditionalLocations = usePrevious(additionalLocations);

  useEffect(() => {
    // BLS tenants skip the Want-Listing-vs-inventory prompt - they're
    // dropped on the feed directly per the skip-onboarding policy.
    if (showAgentModal && !mlsRestricted) dispatch(showAgentPreferenceModal());
  }, [showAgentModal, dispatch, mlsRestricted]);


  useEffect(() => {
    if (isGitchaGenieFilter === true) {
      fetchWantAdsMatchingGitchaGenie(filterQueryGitchaGenie);
    }
  }, [isGitchaGenieFilter]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (
      !isGitchaGenieFilter &&
      (!_.isEqual(previousUserLocation, userLocation) ||
        !_.isEqual(previousAdditionalLocations, additionalLocations))
    ) {
      updateQuery({
        lat: _.get(userLocation, "latitude", null),
        long: _.get(userLocation, "longitude", null),
        city: _.get(userLocation, "city", null),
        state: _.get(userLocation, "state", null),
        locations: buildLocationsParam(userLocation, additionalLocations),
      });
      setCurrPage(1);
      setAdList([]);
    }
  }, [userLocation, additionalLocations]);

  useEffect(() => {
    // BLS tenants bypass the Gitcha "available cities" gate - the MLS owns
    // the market, picked or not.
    if (
      !isGitchaGenieFilter &&
      (userLocation?.isFromAvailableCities || isBlsFeed) &&
      user
    ) {
      fetchWantAds(filterQuery);
    }
  }, [filterQuery, isGitchaGenieFilter]);

  useEffect(() => {
    if (!user) {
      fetchWantAdsPublic(filterQuery);
    }
  }, [filterQuery]);

  useEffect(() => {
    if (isGitchaGenieFilter) {
      fetchWantAdsMatchingGitchaGenie(filterQueryGitchaGenie);
    }
  }, [
    isGitchaGenieFilter,
    filterQueryGitchaGenie.inventoryId,
    filterQueryGitchaGenie.mlsListingKey,
    filterQueryGitchaGenie.priceMin,
    filterQueryGitchaGenie.sortType,
    filterQueryGitchaGenie.priceMax,
    filterQueryGitchaGenie.pricePoint,
    filterQueryGitchaGenie.page,
    filterQueryGitchaGenie.adType,
    filterQueryGitchaGenie.search,
    filterQueryGitchaGenie.propertyType,
    filterQueryGitchaGenie.bedrooms,
    filterQueryGitchaGenie.baths,
    filterQueryGitchaGenie.sqrftMin,
    filterQueryGitchaGenie.sqrftMax,
    filterQueryGitchaGenie.financeType,
    filterQueryGitchaGenie.timeline,
    filterQueryGitchaGenie.purchaseType,
    filterQueryGitchaGenie.preApproved,
    reload == true,
  ]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    // The rebuilt query can be identical to the last one, and this clears the
    // list - without forgetting it the refetch is skipped and the feed stays
    // empty.
    lastFeedQueryRef.current = null;
    updateQuery(initialQuery);
    setCurrPage(1);
    setAdList([]);
    let showNotification = checkTutorialDisplayStatus(tutorialPage);
  }, [sessionData?.userInfo?.userId]);

  useEffect(() => {
    setTimeout(() => {
      setSessionData(getLocalStorage(USER_INFO_KEY));
    }, 1500);
  }, [user?.token?.accessToken]);

  // Refresh whether this user has any want-listing syndicated to the public.
  // Drives the BLS feed share-icon behaviour (modal vs copy-link) and is
  // re-fetched on each feed mount so it stays live within the session.
  useEffect(() => {
    const refreshSyndicationStatus = async () => {
      if (!user?.userInfo?.userId) return;
      try {
        const response = await getHasPublicSyndication();
        if (response?.errorCode === 0) {
          const hasSyndicated = _.get(
            response,
            "result.hasSyndicatedToPublic",
            false,
          );
          const userDetails = getLocalStorage(USER_INFO_KEY);
          if (userDetails?.userInfo) {
            userDetails.userInfo.hasSyndicatedToPublic = hasSyndicated;
            setLocalStorage(USER_INFO_KEY, userDetails);
          }
        }
      } catch (err) {
        console.log("Error", err);
      }
    };
    refreshSyndicationStatus();
  }, [user?.userInfo?.userId]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ page: currPage });
    } else {
      updateQuery({ page: currPage });
    }
  }, [currPage]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ sortType: sortParam?.value });
    } else {
      // "Best match" is a match-mode sort; the feed has no relevance order.
      updateQuery({
        sortType:
          sortParam?.value === "bestMatch" ? "newest" : sortParam?.value,
      });
    }
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
    setAiSuggestionsGenie([]);
  }, [sortParam]);

  // Entering match mode (a property picked) resets the sort to Best match so
  // the AI ordering is the default; leaving it restores a feed sort. The user
  // can still pick Newest/price sorts inside match mode and they are honored
  // (the server answers with the matcher list, aiStatus 'filtered').
  useEffect(() => {
    if (isRestoredRef.current) return;
    if (selectedProperty) {
      setSortParam(MATCH_SORT_DEFAULT);
    } else if (sortParam?.value === "bestMatch") {
      setSortParam(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.ListingKey, selectedProperty?.Inv_Id]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    updateQueryGitchaGenie({
      inventoryId:
        selectedProperty?.rowType === "mls" ? null : selectedProperty?.Inv_Id,
      mlsListingKey:
        selectedProperty?.rowType === "mls" ? selectedProperty?.ListingKey : null,
      adType: adType,
      priceMin: priceMinGitchaGenieFromStorage || PRICE_MIN,
      priceMax: priceMaxGitchaGenieFromStorage || PRICE_MAX,
      city: selectedProperty?.City,
      state: selectedProperty?.State,
      sortType: sortParam?.value,
      page: currPage,
      limit: WANT_AD_PAGE_LIMIT,
    });
    // Full replace, not updateQuery's merge: initialQuery is a fresh query
    // for the new adType and intentionally omits filters that don't apply to
    // it (financeType, preApproved, timeline, purchaseType) - merging would
    // silently carry those over from the previous adType.
    setFilterQuery(initialQuery);
    saveFilterSession(initialQuery, userLocation, sortParam, additionalLocations);
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
    setAiSuggestionsGenie([]);
  }, [adType]);

  // Location is the feed's context, not a filter. Clearing filters used to null
  // it, which stranded the agent on the location-search screen with no way
  // back. initialQuery already derives lat/long/city/state/locations from the
  // current userLocation, so the market survives a filter reset untouched.
  const clearFilterQuery = () => {
    sessionStorage.removeItem("homeFilterSession");
    setCurrPage(1);
    setAdType(BUY);
    setFilterQuery(initialQuery);
  };

  const clearFilterGitchaGenie = () => {
    setFilterQueryGitchaGenie(initialQueryGitchaGenie);
  };

  /**
   * function to call Ad list
   * @param {*} query
   */
  const fetchWantAds = async (query) => {
    const hasCoords = !!(_.get(query, "lat", "") && _.get(query, "long", ""));
    // The API answers a query with no lat/long/locations with everything in
    // that MLS. That is the BLS default; on Gitcha it would mean the whole
    // country, so coords stay required there.
    if (hasCoords || isBlsFeed) {
      const queryKey = JSON.stringify(query);
      if (lastFeedQueryRef.current === queryKey) return;
      lastFeedQueryRef.current = queryKey;
      if (query.page === 1) setIsFeedLoading(true);
      await getWantAds(query)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              if (responseResult.length > 0) {
                setShowNoResult(false);
                if (query.page === 1) {
                  setAdList(responseResult);
                  resetScrollToTop();
                } else {
                  setAdList((prev) => appendWantAdPage(prev, responseResult));
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                query.page === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
          // Clear loading for every resolved response - including a non-zero
          // errorCode - so the empty state can take over instead of the skeleton.
          if (query.page === 1) setIsFeedLoading(false);
          else setIsFetchingMore(false);
        })
        .catch((err) => {
          console.log("Error", err);
          if (mlsRestricted && query.page === 1) {
            setShowNoResult(true);
            setAdList([]);
          }
          if (query.page === 1) setIsFeedLoading(false);
          else setIsFetchingMore(false);
          // Otherwise retrying the same page is swallowed as a duplicate.
          lastFeedQueryRef.current = null;
        });
    } else if (query.page === 1) {
      // No coords means no request is ever made; without this the feed stays
      // on the loading skeleton forever.
      setIsFeedLoading(false);
    } else {
      setIsFetchingMore(false);
    }
  };

  const fetchWantAdsPublic = async (query) => {
    if (_.get(query, "lat", "") && _.get(query, "long", "")) {
      if (query.page === 1) setIsFeedLoading(true);
      await getWantAdsPublic(query)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              if (responseResult.length > 0) {
                setShowNoResult(false);
                if (query.page === 1) {
                  setAdList(responseResult);
                  resetScrollToTop();
                } else {
                  setAdList((prev) => appendWantAdPage(prev, responseResult));
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                query.page === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
          if (query.page === 1) setIsFeedLoading(false);
          else setIsFetchingMore(false);
        })
        .catch((err) => {
          console.log("Error", err);
          if (query.page === 1) setIsFeedLoading(false);
          else setIsFetchingMore(false);
        });
    } else if (query.page === 1) {
      setIsFeedLoading(false);
    } else {
      setIsFetchingMore(false);
    }
  };

  /**
 * To fetch match want ads
 * @param {*} query
 */
  const fetchWantAdsMatchingGitchaGenie = async (query) => {
    // Bump first so any earlier in-flight request (for a previously
    // selected property) is marked stale even when this call returns early.
    const requestId = ++matchRequestIdRef.current;

    if (!query.inventoryId && !query.mlsListingKey) {
      setShowNoResult(true);
      setTotalCountMatch(null);
      setIsFetchingMore(false);
      return;
    }

    // A DIFFERENT property was selected: drop the previous list so the
    // skeleton shows instead of the old property's matches dimming. Same-key
    // refetches (pagination) keep the visible list.
    const selectionKey = query.mlsListingKey || `inv:${query.inventoryId}`;
    if (aiSelectionKeyRef.current !== selectionKey) {
      aiSelectionKeyRef.current = selectionKey;
      if (query.page === 1) {
        setAdListgitchaGenie([]);
        setAiSuggestionsGenie([]);
        setTotalCountMatch(null);
      }
    }

    setShowNoResult(false);
    setIsLoadingMatches(true);

    const mapMatchSort = (val) => {
      switch ((val || "").toLowerCase()) {
        case "newest":
          return "MOST_RECENT";
        case "oldest":
          return "OLDEST";
        // price sorts are not applicable in match mode, default to relevance
        case "highprice":
        case "lowprice":
        case "bestmatch":
        default:
          return "MOST_RELEVANT";
      }
    };

    try {
      const isMls = !!query.mlsListingKey;
      const request = {
        page: query.page,
        limit: query.limit,
        sortType: mapMatchSort(query.sortType),
        minScore: isMls ? 50 : 60,
        userId: getUserId(),
        ...(isMls
          ? { mlsListingKey: query.mlsListingKey }
          : { inventoryId: query.inventoryId }),
        ...(query.pricePoint != null && { pricePoint: query.pricePoint }),
        ...(query.search && { search: query.search }),
        ...(Array.isArray(query.propertyType) &&
          query.propertyType.length > 0 && {
          propertyType: query.propertyType,
        }),
        ...(query.bedrooms && { bedrooms: query.bedrooms }),
        ...(query.baths && { baths: query.baths }),
        ...(query.sqrftMin != null && { sqrftMin: query.sqrftMin }),
        ...(query.sqrftMax != null && { sqrftMax: query.sqrftMax }),
        ...(Array.isArray(query.financeType) &&
          query.financeType.length > 0 && {
          financeType: query.financeType,
        }),
        ...(Array.isArray(query.timeline) &&
          query.timeline.length > 0 && {
          timeline: query.timeline,
        }),
        ...(Array.isArray(query.purchaseType) &&
          query.purchaseType.length > 0 && {
          purchaseType: query.purchaseType,
        }),
        ...(query.preApproved && { preApproved: query.preApproved }),
      };

      const response = await getMatches(request);
      // A newer selection/request has started since this one was issued -
      // drop this response rather than let it overwrite the newer one.
      if (requestId !== matchRequestIdRef.current) return;

      const payload = _.get(response, "data.data", []);
      const totalCount = _.get(response, "data.totalCount", 0);
      // The server says explicitly whether an AI list was served ('ranked'),
      // is still computing ('pending'), or is never coming for this request
      // ('none' / 'filtered'). The strip and the silent refetches key on
      // 'pending' ONLY — a no-coverage listing never shows a promise that
      // cannot be kept.
      const aiStatus = _.get(response, "data.aiStatus", "none");

      setShowGitchaGeineLabel(true);
      setTotalCountMatch(totalCount);

      // The moment the AI list lands, refresh the decorations query too -
      // its 5-minute cache otherwise serves the pre-rank empty answer and
      // the AI rows render as plain legacy-looking cards.
      if (query.page === 1 && aiStatus === "ranked") {
        queryClient.invalidateQueries({ queryKey: ["aiRankingForListing"] });
      }
      // R6b: no retry cycle. Opening a property starts no computation, so
      // there is nothing to poll for — a property is only selectable once its
      // Suggestions ride on page 1 only - a fixed tail under the Best fits.
      const suggestions =
        query.page === 1 ? _.get(response, "data.suggestions", []) || [] : null;
      if (suggestions !== null) setAiSuggestionsGenie(suggestions);

      if (Array.isArray(payload) && payload.length > 0) {
        if (query.page === 1) {
          setAdListgitchaGenie([...payload]);
          resetScrollToTop();
        } else {
          setAdListgitchaGenie((prev) => appendWantAdPage(prev, payload));
        }
        setShowNoResult(false);
      } else if (query.page === 1) {
        setAdListgitchaGenie([]);
        // A page with only suggestions is still a page with content.
        setShowNoResult(!(suggestions && suggestions.length > 0));
      }
    } catch (err) {
      if (requestId !== matchRequestIdRef.current) return;
      toast.dismiss();
      toast.error(err?.message || "Failed to load matches");
      setAdListgitchaGenie([]);
      setAiSuggestionsGenie([]);
      setShowNoResult(true);
      setTotalCountMatch(0);
    } finally {
      if (requestId === matchRequestIdRef.current) setIsLoadingMatches(false);
      setIsFetchingMore(false);
    }
  };


  const updateFilter = (stateObject) => {
    isRestoredRef.current = false;
    setCurrPage(1);
    updateQuery(stateObject);
  };

  // Selecting a property is what ENTERS match mode, so isGitchaGenieFilter is
  // still false on the tick the selection fires. Gating the query update on it
  // dropped inventoryId, so the match fetch bailed on its own guard clause,
  // left totalCountMatch null, and pinned the feed on a skeleton that never
  // resolved. The query is only ever read in match mode, so keeping it in sync
  // unconditionally is safe.
  const updateFilterGitchaGenie = (stateObject) => {
    if (isGitchaGenieFilter) setCurrPage(1);
    updateQueryGitchaGenie(stateObject);
  };

  const updateQuery = (stateObject) => {
    // Functional update: two updateQuery calls can land in the same commit
    // (e.g. the adType-change filter reset in LeftpanelTabContent racing
    // Home's own adType effect below) - closing over `filterQuery` here would
    // let whichever call runs second silently discard the first's changes.
    setFilterQuery((prevFilterQuery) => {
      const newFilterQuery = { ...prevFilterQuery, ...stateObject };
      saveFilterSession(newFilterQuery, userLocation, sortParam, additionalLocations);
      return newFilterQuery;
    });
  };

  const updateQueryGitchaGenie = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setFilterQueryGitchaGenie((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const prevScrollPosition = usePrevious(scrollPosition);
  /**
   * To handle scroll
   */
  const handleScroll = () => {
    const el = myRef.current;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;

    // if (
    //   scrollHeight !== clientHeight &&
    //   scrollTop !== 0 &&
    //   Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1
    // ) {
    //   setShowFooter(true);
    //   setScrollDirection("down");
    // } else if (scrollTop < prevScrollPosition && scrollDirection !== "up") {
    //   setShowFooter(false);
    //   setScrollDirection("up");
    // }
    // setScrollPosition(scrollTop);
  };

  // currPage and the query page must move in the same commit: routing the
  // page through the currPage effect alone let anything that reset currPage
  // or replaced filterQuery in between swallow the click.
  const handleShowMoreClick = () => {
    if (isFetchingMore) return;
    isRestoredRef.current = false;
    setShowFooter(false);
    setIsFetchingMore(true);
    const nextPage = currPage + 1;
    setCurrPage(nextPage);
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ page: nextPage });
    } else {
      updateQuery({ page: nextPage });
    }
  };

  /**
   * To handle the want-ad favorite/unfavorite
   */

  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (Want_ad_Id) {
      let data = {
        clientId: IsFavourite == false ? clientId : null,
        wantAdId: Want_ad_Id,
      };
      await (
        IsFavourite == false ? favouriteWantAd(data) : unFavouriteWantAd(data)
      )
        .then((response) => {
          if (response.errorCode == 0) {
            let message = "";
            if (clientId.length > 0) {
              message =
                "Want-Listing has been favorited for " +
                clientId.length +
                (clientId.length > 1 ? " clients" : " client");
            } else {
              message =
                IsFavourite == true
                  ? "Want-Listing has been removed from your favorites"
                  : "Want-Listing has been added to your favorites";
            }

            const favToastId = "feed-favorite-toast";
            toast.dismiss(favToastId);
            toast.success(message, { toastId: favToastId, autoClose: 1000 });

            const newFavValue = IsFavourite == false ? 1 : 0;
            const applyFav = (items) =>
              (items || []).map((item) =>
                item.Want_ad_Id == Want_ad_Id
                  ? { ...item, IsFavourite: newFavValue }
                  : item,
              );
            setAdList((prev) => applyFav(prev));
            setAdListgitchaGenie((prev) => applyFav(prev));
            setAiSuggestionsGenie((prev) => applyFav(prev));
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  // Remove existing all localStorage keys except userInfo.
  // userLocation is deliberately excluded - it's the one key
  // clearLocalStorageOnLogout also preserves, so a returning user isn't
  // re-asked for their market on every visit.
  useEffect(() => {
    removeMultipleLocalStorageItems([
      WANT_AD_KEY,
      INVENTORY_KEY,
      STRENGTHEN_INVENTORY_KEY,
      CLIENT_DETAILS_KEY,
    ]);
  }, []);

  /**
   *
   */
  useEffect(() => {
    getAgentsClientsList();
  }, [formData, reloadAgents, sessionData?.userInfo?.userId]);

  /**
   * To handle textbox value change on agent client search
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = "";
    if (event) {
      value = handleTextBoxChange(event);
    }

    setFormData((formData) => value);
  };

  /**
   * To get agents clients list
   * @param {*} query
   */
  const getAgentsClientsList = async (query) => {
    let a = {
      search: formData,
      sortBy: "createdOn",
      sortDir: "desc",
    };
    await getAgentsList(a)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setResults(responseResult);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleWantAdItemClick = (wantAdId, slug) => {
    window.history.scrollRestoration = "manual";
    const sessionUserInfo = getLocalStorage(USER_INFO_KEY);
    // The listing whose matches the agent was reading. Carried over so the
    // introduction picker opens on it instead of making them find it again.
    const navState =
      selectedProperty?.rowType === "mls" && selectedProperty?.ListingKey
        ? { state: { preselectListingKey: selectedProperty.ListingKey } }
        : undefined;
    if (sessionUserInfo?.userInfo?.userId) {
      setLocalStorage(WANT_AD_KEY, {
        wantAdId: wantAdId,
        isFromHomeFeed: true,
      });
      navigate(`${pageURLs.wantAdDetails}/${slug}`, navState);
    } else {
      navigate(`${pageURLs.wantAdDetails}/${slug}`, navState);
    }
  };

  const handleGitchGenieFilter = (value) => {
    setIsGitchaGenieFilter(value);
  };

  const handleSortParam = (selectedParam) => {
    isRestoredRef.current = false;
    setSortParam(selectedParam);
  };
  const getLocationText = () => {
    if (isGitchaGenieFilter) {
      return totalCountMatch != null && totalCountMatch > 0
        ? `${totalCountMatch} Matches to Want-Listings`
        : "";
    } else {
      if (totalCount == null) return "";
      const audience = adType == BUY ? "Buyer" : "Renter";
      const marketLabel = buildLocationLabel(userLocation, additionalLocations);
      if (marketLabel) {
        return `${audience} Want-Listings In/Near ${marketLabel}`;
      }
      return tenant?.mlsShortName
        ? `All ${audience} Want-Listings in ${tenant.mlsShortName}`
        : `All ${audience} Want-Listings`;
    }
  };

  const ref = useRef(null);

  const handleOnMouseEnter = () => {
    setMatchTutorial(true);
  };
  const handleToggle = async () => {
    let tutorialDisplayedPages = getLocalStorage("tutorialDisplayedPages");
    tutorialDisplayedPages = tutorialDisplayedPages
      ? tutorialDisplayedPages
      : [];
    tutorialDisplayedPages.push(1); //
    setLocalStorage("tutorialDisplayedPages", tutorialDisplayedPages);
    setNotificationIcon(false);
    if (sessionData && sessionData?.userInfo?.userId) {
      await saveTutorialStatus(1, 1); //Save status
    }
  };
  const [showWelcomeModal, setShowWelcomeModal] = useState();

  const handleCloseModal = () => {
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    if (
      showNotification === true &&
      sessionData &&
      sessionData?.userInfo?.userId
    ) {
      checkUserInventoryCount();
    }
  }, []);

  const checkUserInventoryCount = async () => {
    let inventoryCount = await getUserInventoryCount();
    const responseResult = _.get(inventoryCount, "result", null);
    setNotificationIcon(false);
    if (inventoryCount.errorCode == 0 && responseResult) {
      if (responseResult[0].count > 0) {
        setNotificationIcon(true);
      }
    }
  };
  /**
   * To check gitcha is available in searched location
   * @param {*} city
   * @param {*} state
   * @param {*} county
   * @param {*} formatted_address
   * @param {*} locationObject
   */
  const checkGitchaAvailableInLoc = async (
    city,
    state,
    county,
    formatted_address,
    locationObject,
  ) => {
    isRestoredRef.current = false;
    try {
      const reqPayLoad = {
        county: county,
        state: state,
        city: city,
      };
      const response = await checkGitchaAvailable(reqPayLoad);
      const decision = resolveLocationSelection({
        userLocation,
        additionalLocations,
        isAvailable: !!response.result,
        locationObject,
      });

      if (decision.type === "replacePrimary") {
        setUserLocation(decision.userLocation);
        if (decision.userLocation.isFromAvailableCities) {
          setGitchaNotAvailable(false);
        } else {
          setGitchaNotAvailable(true);
          setShowGitchaNotAvailablePopup(true);
        }
      } else if (decision.type === "addAdditional") {
        setAdditionalLocations(decision.additionalLocations);
      }
      // decision.type === "noop": unavailable/duplicate/over-cap
      // additional-city pick - no state change.
    } catch (error) {
      if (!userLocation?.isFromAvailableCities) {
        setGitchaNotAvailable(true);
        setShowGitchaNotAvailablePopup(true);
      }
      // Additional-city failures fail silently, same as a flaky network
      // call while typing - the chip just doesn't get added.
    }
  };

  // index 0 = primary (userLocation); 1..N = additionalLocations[index - 1].
  useEffect(() => {
    const open = () => {
      if (!userId || !mlsRestricted || userType !== USER_TYPE_AGENT) return;
      // Opening against a loading skeleton means every targeted step fails
      // its visibility check and gets filtered out, same as auto-open avoids
      // below - so defer until the feed has actually rendered.
      if (isFeedLoading) {
        whatsNewPendingRef.current = true;
        return;
      }
      setWhatsNewOpen(true);
    };
    window.addEventListener("bls:whats-new", open);
    return () => window.removeEventListener("bls:whats-new", open);
  }, [userId, mlsRestricted, userType, isFeedLoading]);

  // Drains a re-entry request that arrived while the feed was still loading.
  useEffect(() => {
    if (isFeedLoading) return;
    if (!whatsNewPendingRef.current) return;
    whatsNewPendingRef.current = false;
    setWhatsNewOpen(true);
  }, [isFeedLoading]);

  // Auto-open once per release, and only once the feed has rendered - the tour
  // measures real elements, so opening it against a loading skeleton
  // spotlights the wrong boxes.
  useEffect(() => {
    // Signed-in BLS agents only. This describes BLS controls and links to a
    // BLS release page, so it has no business appearing for a logged-out
    // visitor or for a consumer on Gitcha.
    if (!userId || !mlsRestricted || userType !== USER_TYPE_AGENT)
      return undefined;
    if (isFeedLoading) return undefined;
    if (whatsNewAutoOpenedRef.current) return undefined;
    // ?whatsnew=1 forces it open and ignores the seen flag, so the tour can be
    // demoed or re-checked without clearing storage by hand.
    const forced = searchParams.get("whatsnew") === "1";
    // Either store having recorded it is enough. tutorialDisplayedPages mirrors
    // the Tutorials rows and is refilled from them at every login, so it carries
    // "seen" across a cleared cache - clearing storage signs the agent out, and
    // the next login re-reads the row. SEEN_STORAGE_KEY answers within a session
    // that has not re-authenticated since.
    if (hasSeenRelease() && !forced) return undefined;
    // Marked as opened only when it actually opens. Setting it before the
    // timer meant any dependency change inside that 900ms cancelled the timer
    // while the marker stayed set, and the tour could then never appear.
    const t = setTimeout(() => {
      whatsNewAutoOpenedRef.current = true;
      setWhatsNewOpen(true);
      // Otherwise ?whatsnew=1 outlives the tour it forced open - it survives
      // dismissal, so every later reload or remount reopens the tour again.
      if (forced) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("whatsnew");
            return next;
          },
          { replace: true },
        );
      }
    }, 900);
    return () => clearTimeout(t);
    // Deliberately keyed to userId, not `user` - see its definition. Depending
    // on the object cancelled this timer on every render, so the tour only
    // opened if the feed went a clear 900ms without one, which it usually does
    // not.
  }, [
    userId,
    mlsRestricted,
    userType,
    isFeedLoading,
    searchParams,
    setSearchParams,
  ]);

  const removeLocationAt = (index) => {
    if (index === 0) {
      if (additionalLocations.length > 0) {
        const [next, ...rest] = additionalLocations;
        setUserLocation({ ...next, isFromAvailableCities: true });
        setAdditionalLocations(rest);
      } else {
        setUserLocation(null);
      }
    } else {
      setAdditionalLocations((prev) => prev.filter((_, i) => i !== index - 1));
    }
  };

  const currentSelectedProperty = getLocalStorage("selectedPropertyId");

  const renderMatchNoPropertyState = () => (
    <div className="no-result-wrap">
      <h3>Add a property to see your matches</h3>
      <p>
        Once you add a property we’ll show you the Want-Listings agents have
        posted that match it, and notify you as new ones come in.
      </p>
    </div>
  );

  const renderNoMatchesYetState = () => (
    <div className="no-result-wrap">
      <Button
        className="filter-button filter-icon-btn"
        onClick={() => openDrawer("filters")}
      >
        <img src={FilterIcon} alt="Filters" />
      </Button>
      <>
        <h3>
          It looks like your property doesn’t have any matches yet, but we’ll
          notify you as soon as you have a match!
        </h3>
        <p>
          In the meantime, you can add another property or post a want-listing.
        </p>
      </>
    </div>
  );

  /**
   * To handle rendering result
   * @returns
   */
  const renderResult = () => {
    if (isGitchaGenieFilter) {
      const noMatchesLoaded =
        adListGitchaGenie && adListGitchaGenie.length === 0;

      // Before every other branch: a newly onboarded agent whose matches are
      // still being built has no property to show yet, and must not be told
      // to "add a property" — theirs are on the way.
      if (aiSync.isGated) {
        return (
          <AiSyncBanner
            gated
            settled={aiSync.settled}
            total={aiSync.total}
          />
        );
      }

      // Before any skeleton branch: with no property there is no inventoryId,
      // so fetchWantAdsMatchingGitchaGenie never fires and nothing resolves.
      if (
        matchInventory.resolved &&
        !matchInventory.hasProperties &&
        noMatchesLoaded
      ) {
        return renderMatchNoPropertyState();
      }

      if (
        showNoResult &&
        noMatchesLoaded &&
        currentSelectedProperty?.Inv_Id == null
      ) {
        return <MatchHomeTabSkeleton />;
      } else if (
        showNoResult &&
        noMatchesLoaded &&
        currentSelectedProperty?.Inv_Id
      ) {
        if (isLoadingMatches || totalCountMatch == null) {
          return <MatchHomeTabSkeleton />;
        }
        // Previously gated on userType/isPremiumUser, which returned undefined
        // and rendered a blank feed for half-provisioned accounts.
        return (
          <>
            {aiSync.isSyncing && (
              <AiSyncBanner
                gated={false}
                settled={aiSync.settled}
                total={aiSync.total}
              />
            )}
            {renderNoMatchesYetState()}
          </>
        );
      } else if (
        !showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0 &&
        aiSuggestionsGenie.length === 0
      ) {
        return (
          <>
            <div
              className={
                !showFooter
                  ? "property-listing-outer "
                  : "property-listing-outer"
              }
              onScroll={handleScroll}
              ref={myRef}
            >
              <HomeLoadingSkeleton />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer " : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            {aiSync.isSyncing && (
              <AiSyncBanner
                gated={false}
                settled={aiSync.settled}
                total={aiSync.total}
              />
            )}
            <div style={{ opacity: isLoadingMatches ? 0.5 : 1, transition: "opacity 0.2s ease", pointerEvents: isLoadingMatches ? "none" : "auto" }}>
              {sortedAdListGitchaGenie &&
                sortedAdListGitchaGenie.map((wa, index) => (
                  <PropertyListing
                    list={wa}
                    key={index}
                    ai={
                      listingAiRank.byWantAdId.get(Number(wa?.Want_ad_Id)) ||
                      null
                    }
                    handleFavoriteOrUnfavoriteWantAd={
                      handleFavoriteOrUnfavoriteWantAd
                    }
                    results={results}
                    clientSearchText={formData}
                    onChangeText={onChangeText}
                    onAddClient={() => setReloadAgents(!reloadAgents)}
                    handleWantAdItemClick={handleWantAdItemClick}
                  />
                ))}
              {showGitchaGeineLabel &&
                wantAdCountGitchaGenie < totalCountMatch && (
                  <div className="text-center pt-3 pb-3 w-100">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleShowMoreClick()}
                      disabled={isFetchingMore}
                    >
                      {isFetchingMore ? "Loading..." : "Show More"}
                    </button>
                  </div>
                )}
              {!(wantAdCountGitchaGenie < totalCountMatch) &&
                aiSuggestionsGenie.length > 0 && (
                  <>
                    {/* Same control as the want-ad details page, so the two
                        surfaces reveal suggestions the same way. */}
                    {!showAiSuggestions && (
                      <div className="text-center pt-3 pb-3 w-100">
                        <button
                          type="button"
                          className="ai-suggestions-toggle"
                          aria-expanded={false}
                          onClick={() => setShowAiSuggestions(true)}
                        >
                          More suggestions ({aiSuggestionsGenie.length})
                        </button>
                      </div>
                    )}
                    {showAiSuggestions &&
                      sortedAiSuggestionsGenie.map((wa, index) => (
                      <PropertyListing
                        list={wa}
                        key={`sugg-${wa?.Want_ad_Id || index}`}
                        ai={
                          listingAiRank.byWantAdId.get(
                            Number(wa?.Want_ad_Id),
                          ) || null
                        }
                        handleFavoriteOrUnfavoriteWantAd={
                          handleFavoriteOrUnfavoriteWantAd
                        }
                        results={results}
                        clientSearchText={formData}
                        onChangeText={onChangeText}
                        onAddClient={() => setReloadAgents(!reloadAgents)}
                        handleWantAdItemClick={handleWantAdItemClick}
                      />
                      ))}
                  </>
                )}
            </div>
          </div>
        );
      }
    } else {
      if (!userLocation?.isFromAvailableCities && !isBlsFeed) {
        return (
          <>
            <div className="no-result-wrap">
              <GitchaAvailableLocations
                setUserLocation={setUserLocation}
                userLocation={userLocation}
                checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
              />
            </div>
          </>
        );
      } else if (
        mlsRestricted &&
        (searchParams.get("showEmptyState") === "1" ||
          (!isFeedLoading &&
            adList &&
            adList.length === 0 &&
            activeFilterCount === 0))
      ) {
        return (
          <div className="property-listing-outer">
            <FeedEmptyState
              onCta={() =>
                navigate(pageURLs.agentCreatewantadMainScreen)
              }
            />
          </div>
        );
      } else if (adList && adList.length === 0) {
        // Only skeleton while a fetch is actually in flight. A response with a
        // non-zero errorCode leaves showNoResult false, which used to pin this
        // branch on the skeleton with nothing left to resolve it.
        if (isFeedLoading) {
          return (
            <>
              <div
                className={
                  !showFooter
                    ? "property-listing-outer "
                    : "property-listing-outer"
                }
                onScroll={handleScroll}
                ref={myRef}
              >
                <HomeLoadingSkeleton />
              </div>
            </>
          );
        }
        return (
          <div className="no-result-wrap">
            <Button
              className="filter-button filter-icon-btn"
              onClick={() => openDrawer("filters")}
            >
              <img src={FilterIcon} alt="Filters" />
            </Button>
            {activeFilterCount > 0 ? (
              <>
                <h3>No Want-Listings match these filters</h3>
                <p>Try removing a filter to widen the search.</p>
                <Button
                  className="btn btn-primary mt-2"
                  onClick={() => clearFilterQuery()}
                >
                  Clear all filters
                </Button>
              </>
            ) : (
              userLocation ? (
                <>
                  <h3>There aren't any want-listings in your location yet</h3>
                  <p>In the mean time, try broadening your location search</p>
                </>
              ) : (
                <>
                  <h3>There aren't any want-listings yet</h3>
                  <p>New Want-Listings show up here as agents post them.</p>
                </>
              )
            )}
          </div>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer " : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            {adList &&
              adList.map((wa, index) => (
                <PropertyListing
                  list={wa}
                  key={index}
                  ai={
                    listingAiRank.byWantAdId.get(Number(wa?.Want_ad_Id)) || null
                  }
                  handleFavoriteOrUnfavoriteWantAd={
                    handleFavoriteOrUnfavoriteWantAd
                  }
                  results={results}
                  clientSearchText={formData}
                  onChangeText={onChangeText}
                  onAddClient={() => setReloadAgents(!reloadAgents)}
                  handleWantAdItemClick={handleWantAdItemClick}
                />
              ))}
            {wantAdCount < totalCount && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="btn btn-primary"
                  onClick={() => handleShowMoreClick()}
                  disabled={isFetchingMore}
                >
                  {isFetchingMore ? "Loading..." : "Show More"}
                </button>
              </div>
            )}
          </div>
        );
      }
    }
  };

  if (mlsRestricted && !user) {
    return (
      <TenantLoginPage
        ssoError={ssoError}
        mlsLinkState={mlsLinkState}
        onVerifySuccess={() => {
          const dest = safeRedirectPath(mlsLinkState.redirect) || pageURLs.home;
          setMlsLinkState({ show: false, pendingToken: null, maskedEmail: "", redirect: null });
          navigate(dest, { replace: true });
          window.location.reload();
        }}
        onVerifyClose={() => {
          setMlsLinkState({ show: false, pendingToken: null, maskedEmail: "", redirect: null });
          navigate(pageURLs.home, { replace: true });
        }}
      />
    );
  }

  return (
    <>
      {sessionData ? (
        <PreLaunchCityRequestModal
          show={isCityInPreLaunch}
          onHide={() => {
            setIsCityInPreLaunch(false);
            setPreLaunchStateName(null);
          }}
          setUserLocation={setUserLocation}
          userLocation={userLocation}
          PreLaunchStateName={PreLaunchStateName}
        />
      ) : (
        <></>
      )}
      {showWelcomeModal && (
        <WelcomeModal
          name={getLocalStorage("userInfo")?.userInfo?.firstName}
          videoUrl={
            "https://gitcha-uploads.s3.us-east-1.amazonaws.com/public/First+Steps_+Quick+5+Min+Walkthrough+of+Gitcha.mp4"
          }
          showModal={showWelcomeModal}
          onClose={handleCloseModal}
        />
      )}
      <MatchTutorial
        show={showMatchTutorial}
        onHide={() => {
          handleToggle();
          setMatchTutorial(false);
        }}
      />
      <RequestStayAvailability
        show={showRequestStayPopuop}
        locationFromSTorage={browserLocation}
        onHide={handleStayRequestPopupClose}
      />
      <GeoLocation
        handleUserLocation={handleUserLocation}
        page={HOME_PAGE}
        skipGeolocation={skipGeolocationRef.current}
      />
      <filterContext.Provider
        value={{
          // handleAdTypeClick,
          adType: BUY,
          clearFilterQuery,
          adListGitchaGenie,
          totalCountMatch,
          updateQueryGitchaGenie,
          setMatchInventory,
        }}
      >

        <PageWrapper
          headerSidebarProps={{
            onLoginSuccess: () => {
              setCurrPage(1);
              setAdList([]);
              lastFeedQueryRef.current = null;
              const updatedUser = loggedInUser();
              if (
                updatedUser &&
                (userLocation?.isFromAvailableCities || isBlsFeed)
              ) {
                fetchWantAds({ ...filterQuery, page: 1 });
              }
            }
          }}
        >
          {showTransition && (
            <SearchTransition
              show={showTransition}
              cityName={transitionCity}
              onComplete={handleTransitionComplete}
            />
          )}
          {userLocation === null && !isGitchaGenieFilter && !isBlsFeed ? (
            <HeroSplash
              setUserLocation={setUserLocation}
              adType={adType}
              onSearchStart={(locationObj, city, state, county, formatted_address) => {
                setTransitionCity(locationObj.formatted_address);
                setShowTransition(true);
                setTimeout(() => {
                  checkGitchaAvailableInLoc(city, state, county, formatted_address, locationObj);
                }, 100);
              }}
            />
          ) : (
            <div className="d-flex flex-row">
              <Offcanvas
                show={offCanvaShow}
                onHide={() => setOffCanvaShow(false)}
                placement="start"
                className={`pushmenu feed-drawer feed-drawer--${drawerFocus}`}
              >
                <Offcanvas.Header closeButton>
                  <h2>
                    {drawerFocus === "matches" ? "My matches" : "Sort and Filter"}
                  </h2>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <div
                    className="drawer-sort"
                    hidden={drawerFocus !== "filters"}
                  >
                    <label className="feed-field-label" htmlFor="drawer-sort-select">
                      Sort by
                    </label>
                    <SelectDropDown
                      id="drawer-sort-select"
                      name="drawerSort"
                      options={isGitchaGenieFilter ? matchOptions : options}
                      className="basic-single sort-select mb-0"
                      classNamePrefix="select"
                      placeholder="Select"
                      value={sortParam}
                      onChange={handleSortParam}
                      errorClass="d-none"
                    />
                  </div>
                  <div className="left-panel">
                    <LeftpanelTab
                      inDrawer
                      focus={drawerFocus}
                      onDismiss={() => setOffCanvaShow(false)}
                      updateFilter={updateFilter}
                      filterQuery={filterQuery}
                      handleGitchGenieFilter={handleGitchGenieFilter}
                      clearFilterGitchaGenie={clearFilterGitchaGenie}
                      updateFilterGitchaGenie={updateFilterGitchaGenie}
                      selectedProperty={selectedProperty}
                      setSelectedProperty={setSelectedProperty}
                      gaugeDemand={gaugeDemand}
                      setGaugeDemand={setGaugeDemand}
                      setUserLocation={setUserLocation}
                      userLocation={userLocation}
                      additionalLocations={additionalLocations}
                      removeLocationAt={removeLocationAt}
                      gitchaNotAvailble={gitchaNotAvailble}
                      checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                    />
                  </div>
                  {/* Filters apply as they are tapped, so this is feedback and
                      a way out - not a commit gate. Without it the agent has to
                      close the sheet to find out whether they narrowed the feed
                      to nothing. */}
                  {drawerFocus === "filters" && (
                    <div className="feed-drawer__footer">
                      <button
                        type="button"
                        className="feed-drawer__clear"
                        onClick={() => clearFilterQuery()}
                        disabled={activeFilterCount === 0}
                      >
                        Clear all
                      </button>
                      <button
                        type="button"
                        className="feed-drawer__apply"
                        onClick={() => setOffCanvaShow(false)}
                      >
                        {formatResultsCountLabel(totalCount)}
                      </button>
                    </div>
                  )}
                </Offcanvas.Body>
              </Offcanvas>
              <WhatsNew
                open={whatsNewOpen}
                onClose={() => setWhatsNewOpen(false)}
              />
              <div className="pe-md-3 pe-lg-3 pe-xl-4 d-none d-md-block home-left-pannel-wrapper">
                <div className="left-panel position-relative">
                  {showNotificationIcon && (
                    <div className="ring-container">
                      <div className="circlebtn">
                        <div className="ringring"></div>
                      </div>
                    </div>
                  )}

                  <div ref={ref} className="w-100 position-relative">
                    {showNotificationIcon && (
                      <button
                        className="btn red-hotspot-btn position-absolute"
                        onClick={() => setMatchTutorial(true)}
                        onMouseEnter={handleOnMouseEnter}
                      ></button>
                    )}
                  </div>

                  <LeftpanelTab
                    inDrawer={false}
                    updateFilter={updateFilter}
                    filterQuery={filterQuery}
                    handleGitchGenieFilter={handleGitchGenieFilter}
                    clearFilterGitchaGenie={clearFilterGitchaGenie}
                    updateFilterGitchaGenie={updateFilterGitchaGenie}
                    selectedProperty={selectedProperty}
                    setSelectedProperty={setSelectedProperty}
                    gaugeDemand={gaugeDemand}
                    setGaugeDemand={setGaugeDemand}
                    setUserLocation={setUserLocation}
                    userLocation={userLocation}
                    additionalLocations={additionalLocations}
                    removeLocationAt={removeLocationAt}
                    gitchaNotAvailble={gitchaNotAvailble}
                    checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                  />
                </div>
              </div>
              <div className="ps-md-3 ps-lg-3 ps-xl-4 home-listing-wrapper">
                {userLocation === null &&
                  !isGitchaGenieFilter &&
                  !isBlsFeed ? (
                  <SearchLocationScreen
                    setUserLocation={setUserLocation}
                    handleGitchaNotAvailableLocation={() => { }}
                    gitchaNotAvailble={gitchaNotAvailble}
                    checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                    adType={adType}
                    showFilters={() => openDrawer("filters")}
                  />
                ) : (
                  <div className="right-panel">
                    <FeedMobileBar
                      locations={
                        userLocation
                          ? [userLocation, ...(additionalLocations || [])]
                          : []
                      }
                      marketLabel={buildLocationLabel(
                        userLocation,
                        additionalLocations,
                      )}
                      emptyMarketLabel={
                        isBlsFeed
                          ? tenant?.mlsShortName
                            ? `All of ${tenant.mlsShortName}`
                            : "All markets"
                          : undefined
                      }
                      mode={gaugeDemand ? "match" : "all"}
                      marketPickerOpen={marketPickerOpen}
                      onToggleMarketPicker={() => {
                        setMarketPickerOpen(!marketPickerOpen);
                        setMarketSearchText("");
                      }}
                      locationSearch={
                        <FilterLocationSearch
                          id="feed-mobile-bar-location-search"
                          location={marketSearchText}
                          setLocation={setMarketSearchText}
                          updateFilter={updateFilter}
                          adType={adType}
                          gitchaNotAvailble={gitchaNotAvailble}
                          checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                          userLocation={userLocation}
                          setUserLocation={setUserLocation}
                          additionalLocations={additionalLocations}
                          removeLocationAt={removeLocationAt}
                        />
                      }
                      onSelectAll={() => {
                        setGaugeDemand(false);
                        setSelectedProperty(null);
                      }}
                      onSelectMatches={() => {
                        setGaugeDemand(true);
                        openDrawer("matches");
                      }}
                      onOpenFilters={() => openDrawer("filters")}
                      activeFilterCount={activeFilterCount}
                      selectedProperty={selectedProperty}
                      selectedPropertyLabel={
                        selectedProperty?.Location || selectedProperty?.City
                      }
                      matchCount={totalCountMatch}
                      onClearProperty={() => {
                        setSelectedProperty(null);
                        setGaugeDemand(false);
                      }}
                    />
                    <div className="top-sec mb-24 md-mt-24">
                      <div className="d-flex align-items-center results-header-row">
                        <div className="col results-title-col">
                          {!userLocation &&
                            !isGitchaGenieFilter &&
                            !isCityInPreLaunch &&
                            isBlsFeed ? (
                            <h2 className="title-text">{getLocationText()}</h2>
                          ) : (
                            getTitleText(
                              userLocation,
                              isGitchaGenieFilter,
                              isCityInPreLaunch,
                              getLocationText,
                            )
                          )}
                        </div>
                        <div className="d-flex align-items-center results-controls">
                          {/* One control above the feed instead of a sort
                                dropdown beside a filter icon. */}
                          <div className="sort-filter-anchor">
                            <Button
                              className={`sort-filter-btn ${sortFilterOpen ? "is-open" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`}
                              aria-expanded={sortFilterOpen}
                              onClick={() => setSortFilterOpen(!sortFilterOpen)}
                            >
                              <img src={FilterIcon} alt="" aria-hidden="true" />
                              <span>
                                Sort and Filter
                                {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                              </span>
                            </Button>

                            {sortFilterOpen && (
                              <>
                                <div
                                  className="sort-filter-scrim"
                                  onClick={() => setSortFilterOpen(false)}
                                />
                                <div className="sort-filter-panel">
                                  <div className="sort-filter-panel__head">
                                    <div>
                                      <h3>Sort and Filter</h3>
                                      <p>Choose what shows in your feed.</p>
                                    </div>
                                    <button
                                      type="button"
                                      className="sort-filter-panel__close"
                                      aria-label="Close"
                                      onClick={() => setSortFilterOpen(false)}
                                    >
                                      &times;
                                    </button>
                                  </div>
                                  <div className="sort-filter-panel__body">
                                    <div className="drawer-sort">
                                      <label className="feed-field-label" htmlFor="drawer-sort-select">
                                        Sort by
                                      </label>
                                      <SelectDropDown
                                        id="drawer-sort-select"
                                        name="drawerSort"
                                        options={isGitchaGenieFilter ? matchOptions : options}
                                        className="basic-single sort-select mb-0"
                                        classNamePrefix="select"
                                        placeholder="Select"
                                        value={sortParam}
                                        onChange={handleSortParam}
                                        errorClass="d-none"
                                      />
                                    </div>
                                    <LeftpanelTabContent
                                      filtersOnly
                                      updateFilter={
                                        isGitchaGenieFilter
                                          ? updateFilterGitchaGenie
                                          : updateFilter
                                      }
                                      filterQuery={
                                        isGitchaGenieFilter
                                          ? filterQueryGitchaGenie
                                          : filterQuery
                                      }
                                      setUserLocation={setUserLocation}
                                      userLocation={userLocation}
                                      gitchaNotAvailble={gitchaNotAvailble}
                                      checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                                    />
                                  </div>
                                  <div className="sort-filter-panel__foot">
                                    {/* Filters apply on click (updateFilter fires
                                          immediately from each checkbox/toggle) -
                                          "Show results" implied an extra step that
                                          was never actually required, and the scrim
                                          click / header close button already close
                                          the panel. */}
                                    <button
                                      type="button"
                                      className="sort-filter-panel__clear"
                                      onClick={() =>
                                        isGitchaGenieFilter
                                          ? clearFilterGitchaGenie()
                                          : clearFilterQuery()
                                      }
                                    >
                                      Clear all
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Matches mode renders its own banner inside
                        renderResult; here it sits above the feed so a newly
                        onboarded agent sees why their matches are missing on
                        the view they actually land on. */}
                    {!isGitchaGenieFilter && (aiSync.isGated || aiSync.isSyncing) && (
                      <AiSyncBanner
                        gated={aiSync.isGated}
                        settled={aiSync.settled}
                        total={aiSync.total}
                      />
                    )}
                    {renderResult()}
                  </div>
                )}
              </div>
            </div>
          )}
          {!mlsRestricted && <SiteFooter show={showFooter} />}
        </PageWrapper>
        <BackToTopButton />
      </filterContext.Provider>

      <AgentLiketoStartModal />
      <GitchaNotAvailableModal
        show={showGitchaNotAvailablePopup}
        onHide={() => setShowGitchaNotAvailablePopup(false)}
      />
    </>
  );
};

export default Home;